import {
  arrow,
  autoUpdate,
  FloatingArrow,
  offset,
  type Placement,
  useFloating,
} from '@floating-ui/react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { autoPlacement } from '@floating-ui/dom';
import { colors } from '../../../themes';
import {
  PopoverCloseButton,
  PopoverCloseButtonWrapper,
  PopoverContainer,
  PopoverContent,
} from './styles';

export type PopoverProps = {
  children: ReactNode;
  elementId: string;
  show?: boolean;
  darkMode?: boolean;
  placement?: Placement;
  onClose?: () => void;
};

export const Popover = ({
  children,
  elementId,
  show = true,
  darkMode = false,
  placement,
  onClose,
}: PopoverProps) => {
  const arrowRef = useRef<SVGSVGElement | null>(null);

  const [boundaryEl, setBoundaryEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.getElementById('page-container');

    setBoundaryEl(el);
  }, []);

  const { refs, floatingStyles, context } = useFloating({
    open: show,
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      arrow({ element: arrowRef }),
      offset(8),

      autoPlacement({
        padding: 4,
        allowedPlacements: [placement ?? 'bottom-end', 'bottom-start'],
        boundary: boundaryEl ?? undefined, // the external content area
      }),
    ],
  });

  useEffect(() => {
    let frame: number;

    const tryAttach = () => {
      const element = document.getElementById(elementId);

      if (element) {
        refs.setReference(element);

        return;
      }

      frame = requestAnimationFrame(tryAttach);
    };

    tryAttach();

    return () => {
      if (frame) cancelAnimationFrame(frame);
    };
  }, [elementId, refs]);

  if (!show || !refs.reference.current) {
    return null;
  }

  return (
    <PopoverContainer
      ref={(node) => {
        refs.setFloating(node);
      }}
      style={floatingStyles}
      role="tooltip"
      shadow={true}
      darkMode={darkMode}
    >
      {onClose && (
        <PopoverCloseButtonWrapper>
          <PopoverCloseButton
            variant="secondary"
            onClick={onClose}
            aria-label="Close popover"
            darkMode={darkMode}
          >
            <CloseIcon darkMode={darkMode} />
          </PopoverCloseButton>
        </PopoverCloseButtonWrapper>
      )}
      <FloatingArrow
        fill={darkMode ? '#62636A' : colors.base.white}
        ref={arrowRef}
        context={context}
        tipRadius={2}
        height={8}
      />
      <PopoverContent>{children}</PopoverContent>
    </PopoverContainer>
  );
};

export const CloseIcon = ({ darkMode }: { darkMode?: boolean }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      d="M6.575 7.975L1.675 12.875C1.49167 13.0583 1.25833 13.15 0.975 13.15C0.691667 13.15 0.458333 13.0583 0.275 12.875C0.0916667 12.6917 0 12.4583 0 12.175C0 11.8917 0.0916667 11.6583 0.275 11.475L5.175 6.575L0.275 1.675C0.0916667 1.49167 0 1.25833 0 0.975C0 0.691667 0.0916667 0.458333 0.275 0.275C0.458333 0.0916667 0.691667 0 0.975 0C1.25833 0 1.49167 0.0916667 1.675 0.275L6.575 5.175L11.475 0.275C11.6583 0.0916667 11.8917 0 12.175 0C12.4583 0 12.6917 0.0916667 12.875 0.275C13.0583 0.458333 13.15 0.691667 13.15 0.975C13.15 1.25833 13.0583 1.49167 12.875 1.675L7.975 6.575L12.875 11.475C13.0583 11.6583 13.15 11.8917 13.15 12.175C13.15 12.4583 13.0583 12.6917 12.875 12.875C12.6917 13.0583 12.4583 13.15 12.175 13.15C11.8917 13.15 11.6583 13.0583 11.475 12.875L6.575 7.975Z"
      fill={darkMode ? colors.base.white : '#62636A'}
    />
  </svg>
);
